import React from 'react';

const mockData = {
  overallProgress: 75,
  skillMetrics: {
    technical: { current: 80, previous: 65 },
    behavioral: { current: 70, previous: 60 },
    communication: { current: 85, previous: 75 }
  },
  recentInterviews: [
    { date: '2024-03-15', type: 'System Design', score: 85 },
    { date: '2024-03-10', type: 'Algorithms', score: 75 },
    { date: '2024-03-05', type: 'Behavioral', score: 80 }
  ],
  skillBreakdown: {
    'System Design': 85,
    'Data Structures': 70,
    'Algorithms': 75,
    'Problem Solving': 80,
    'Communication': 85
  }
};

const ProgressBar = ({ percentage, label }) => (
  <div className="mb-4">
    <div className="flex justify-between mb-1">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <span className="text-sm font-medium text-gray-700">{percentage}%</span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  </div>
);

const StatCard = ({ title, value, change }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-3xl font-bold text-blue-600">{value}%</p>
    {change && (
      <p className={`text-sm ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
        {change > 0 ? '↑' : '↓'} {Math.abs(change)}% from last month
      </p>
    )}
  </div>
);

const ProgressDashboard = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Interview Progress Dashboard</h1>
      
      {/* Overall Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {Object.entries(mockData.skillMetrics).map(([skill, data]) => (
          <StatCard
            key={skill}
            title={skill.charAt(0).toUpperCase() + skill.slice(1)}
            value={data.current}
            change={data.current - data.previous}
          />
        ))}
      </div>

      {/* Skill Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Skill Breakdown</h2>
        <div className="space-y-4">
          {Object.entries(mockData.skillBreakdown).map(([skill, score]) => (
            <ProgressBar key={skill} label={skill} percentage={score} />
          ))}
        </div>
      </div>

      {/* Recent Interviews */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Recent Interviews</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mockData.recentInterviews.map((interview, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{interview.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{interview.type}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {interview.score}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProgressDashboard; 